import { useEffect, useRef, useState } from "react";
import {
  Star,
  ArrowRight,
  Clock3,
  ShieldCheck,
  BadgeDollarSign,
  CarFront,
  MapPin,
  CalendarDays,
  Users,
  Plane,
  User,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FadeIn,
  SlideUp,
  SlideInLeft,
  StaggerContainer,
  ScaleIn,
} from "../animations/ScrollAnimations";
import "./Hero.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Helmet } from "react-helmet-async";

type TripType = "oneWay";

type AirportOption = {
  value: string;
  label: string;
};

type PickupOption = {
  value: string;
  label: string;
  type: "airport" | "destination";
};

const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  throw new Error("REACT_APP_API_URL is not defined");
}

export function HomeSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const tripType: TripType = "oneWay";

  const [quickForm, setQuickForm] = useState({
    pickup: "",
    destination: "",
    tripDate: "",
    passengers: "1",
  });

  const featuresRef = useRef<HTMLElement | null>(null);
  const [featuresVisible, setFeaturesVisible] = useState(false);

  const [airports, setAirports] = useState<AirportOption[]>([]);
  const [allDestinations, setAllDestinations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  useEffect(() => {
    async function loadLocations() {
      try {
        setLocationsLoading(true);

        const airportsRes = await fetch(`${API_BASE_URL}/api/airports`);

        if (!airportsRes.ok) {
          throw new Error("Falha ao carregar aeroportos");
        }

        const airportsData: AirportOption[] = await airportsRes.json();

        setAirports(airportsData);

        const destinationsByAirport = await Promise.all(
          airportsData.map(async (airport) => {
            try {
              const res = await fetch(
                `${API_BASE_URL}/api/destinations/${encodeURIComponent(
                  airport.value
                )}`
              );

              if (!res.ok) {
                return [];
              }

              const data: string[] = await res.json();

              return data;
            } catch {
              return [];
            }
          })
        );

        const normalizeLocation = (value: string) =>
          value
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, " ");

        const airportBlockedNames = new Set([
          "faro",
          "faro airport",
          "lisbon",
          "lisbon airport",
          "lisboa airport",
          "seville",
          "seville airport",
          "sevilla",
          "sevilla airport",
        ]);

        const uniqueDestinations = Array.from(
          new Set(destinationsByAirport.flat())
        )
          .filter(
            (item) =>
              !airportBlockedNames.has(normalizeLocation(item))
          )
          .sort((a, b) => a.localeCompare(b));

        setAllDestinations(uniqueDestinations);
      } catch (err) {
        console.error("Erro ao carregar localizações:", err);

        setAirports([]);
        setAllDestinations([]);
      } finally {
        setLocationsLoading(false);
      }
    }

    loadLocations();
  }, []);

  useEffect(() => {
    const section = featuresRef.current;

    if (!section) {
      return;
    }

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();

      const sectionIsVisible =
        rect.top < window.innerHeight * 0.72 &&
        rect.bottom > window.innerHeight * 0.25;

      if (sectionIsVisible) {
        setFeaturesVisible(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleQuickChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setQuickForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "pickup" ? { destination: "" } : {}),
    }));
  };

  const handleQuickSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/booking", {
      state: {
        quickBookingData: {
          ...quickForm,
          tripType,
        },
      },
    });
  };

  const pickupOptions: PickupOption[] = [
    ...airports.map((airport) => ({
      value: airport.value,
      label: airport.label,
      type: "airport" as const,
    })),
    ...allDestinations.map((destination) => ({
      value: destination,
      label: destination,
      type: "destination" as const,
    })),
  ];

  const destinationOptions: PickupOption[] = pickupOptions.filter(
    (option) => option.value !== quickForm.pickup
  );

  const features = [
    {
      icon: <CarFront className="w-8 h-8" />,
      title: t("home.features.comfort.title"),
      description: t("home.features.comfort.description"),
    },
    {
      icon: <Clock3 className="w-8 h-8" />,
      title: t("home.features.speed.title"),
      description: t("home.features.speed.description"),
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: t("home.features.drivers.title"),
      description: t("home.features.drivers.description"),
    },
    {
      icon: <BadgeDollarSign className="w-8 h-8" />,
      title: t("home.features.price.title"),
      description: t("home.features.price.description"),
    },
  ];

  const reviews = [
    {
      name: "Sofia Almeida",
      location: "Lisboa",
      rating: 5,
      image: "/reviews/sofia.jpg",
      text: t("home.reviews.items.sofia"),
    },
    {
      name: "Miguel Santos",
      location: "Porto",
      rating: 5,
      image: "/reviews/miguel.jpg",
      text: t("home.reviews.items.miguel"),
    },
    {
      name: "Carolina Martins",
      location: "Faro",
      rating: 5,
      image: "/reviews/carolina.jpg",
      text: t("home.reviews.items.carolina"),
    },
  ];

  const popularRoutes = [
    {
      slug: "faro-airport-to-albufeira",
      destination: "Albufeira",
      price: "45€",
      duration: "30 min",
      image: "/destinations/albufeira.webp",

    },
    {
      slug: "faro-airport-to-vilamoura",
      destination: "Vilamoura",
      price: "40€",
      duration: "30 min",
      image: "/destinations/vilamoura.webp",

    },
    {
      slug: "faro-airport-to-lagos",
      destination: "Lagos",
      price: "90€",
      duration: "1h 10m",
      image: "/destinations/lagos.webp",

    },
    {
      slug: "faro-airport-to-portimao",
      destination: "Portimão",
      price: "70€",
      duration: "1h 00m",
      image: "/destinations/portimao.webp",

    },
    {
      slug: "faro-airport-to-alvor",
      destination: "Alvor",
      price: "80€",
      duration: "1h 05m",
      image: "/destinations/alvor.webp",

    },
    {
      slug: "faro-airport-to-carvoeiro",
      destination: "Carvoeiro",
      price: "65€",
      duration: "1h 00m",
      image: "/destinations/carvoeiro.webp",

    },
  ];

  return (
    <>
    <Helmet>
  <title>{t("seo.home.title")}</title>

  <meta
    name="description"
    content={t("seo.home.description")}
  />

  <link
    rel="canonical"
    href="https://francescotransfers.com/"
  />

   <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "TaxiService"],
      "@id": "https://francescotransfers.com/#business",

      name: "Francesco Transfers",
      alternateName: "Francesco Airport Transfers Algarve",

      url: "https://francescotransfers.com/",
      logo: "https://francescotransfers.com/logo.png",
      image: "https://francescotransfers.com/main.jpg",

      description: t("seo.home.description"),

      telephone: "+351915129868",
      email: "book.tripalgarve@gmail.com",

      priceRange: "€€",

      sameAs: [
        "https://www.instagram.com/francesco_airport_transfers",
        "https://www.facebook.com/profile.php?id=100063527860394",
      ],

      areaServed: [
        {
          "@type": "AdministrativeArea",
          name: "Algarve",
        },
        {
          "@type": "Country",
          name: "Portugal",
        },
        {
          "@type": "Country",
          name: "Spain",
        },
      ],

      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "00:00",
          closes: "23:59",
        },
      ],

      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+351915129868",
        email: "book.tripalgarve@gmail.com",
        contactType: "customer service",
        availableLanguage: [
  "pt",
  "en",
],
        areaServed: [
          "PT",
          "ES",
        ],
      },
    })}
  </script>
</Helmet>
      <div
        className="hero"
        style={{
          backgroundImage: `
            linear-gradient(
              to bottom right,
              rgba(7,17,22,0.72),
              rgba(8,23,28,0.62),
              rgba(7,17,22,0.78)
            ),
            linear-gradient(
              to bottom right,
              rgba(178,245,240,0.14),
              rgba(200,250,245,0.1),
              rgba(235,255,252,0.06)
            ),
            url("/main.jpg")
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="hero-overlay">
          <div className="hero-overlay-gradient" />
        </div>

        <div className="hero-grid">
          <SlideInLeft>
            <div className="hero-text-container">
              <div className="hero-badge">
                <Star className="h-5 w-5 fill-current" />

                <span>{t("home.hero.badge")}</span>
              </div>

              <SlideUp delay={0.4}>
                <h1 className="hero-title">
                  <span className="hero-title-line highlight">
                    {t("home.hero.titleHighlight")}
                  </span>

                  <span className="hero-title-line">
                    {t("home.hero.title")}
                  </span>
                </h1>
              </SlideUp>

              <FadeIn delay={0.6}>
                <p className="hero-paragraph">
                  {t("home.hero.description")}
                </p>
              </FadeIn>

              <SlideUp delay={1}>
                <form
                  className="quick-booking-card"
                  onSubmit={handleQuickSubmit}
                >
                  <div className="quick-booking-top">
                    <div className="quick-booking-header">
                      <Plane size={26} />

                      <h3>{t("home.quickBooking.title")}</h3>
                    </div>
                  </div>

                  <div className="quick-booking-grid">
                    <label className="quick-field">
                      <span>{t("home.quickBooking.pickup")}</span>

                      <div className="quick-input-wrap">
                        <MapPin size={18} />

                        <select
                          name="pickup"
                          value={quickForm.pickup}
                          onChange={handleQuickChange}
                          disabled={locationsLoading}
                        >
                          <option value="">
                            {locationsLoading
                              ? t(
                                  "home.quickBooking.loadingLocations"
                                )
                              : t(
                                  "home.quickBooking.choosePickup"
                                )}
                          </option>

                          <optgroup
                            label={t(
                              "home.quickBooking.airports"
                            )}
                          >
                            {pickupOptions
                              .filter(
                                (option) =>
                                  option.type === "airport"
                              )
                              .map((option) => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </option>
                              ))}
                          </optgroup>

                          <optgroup
                            label={t(
                              "home.quickBooking.destinations"
                            )}
                          >
                            {pickupOptions
                              .filter(
                                (option) =>
                                  option.type === "destination"
                              )
                              .map((option) => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </option>
                              ))}
                          </optgroup>
                        </select>
                      </div>
                    </label>

                    <label className="quick-field">
                      <span>
                        {t("home.quickBooking.destination")}
                      </span>

                      <div className="quick-input-wrap">
                        <MapPin size={18} />

                        <select
                          name="destination"
                          value={quickForm.destination}
                          onChange={handleQuickChange}
                          disabled={
                            !quickForm.pickup || locationsLoading
                          }
                        >
                          <option value="">
                            {!quickForm.pickup
                              ? t(
                                  "home.quickBooking.choosePickupFirst"
                                )
                              : t(
                                  "home.quickBooking.chooseDestination"
                                )}
                          </option>

                          <optgroup
                            label={t(
                              "home.quickBooking.airports"
                            )}
                          >
                            {destinationOptions
                              .filter(
                                (option) =>
                                  option.type === "airport"
                              )
                              .map((option) => (
                                <option
                                  key={`to-airport-${option.value}`}
                                  value={option.value}
                                >
                                  {option.label}
                                </option>
                              ))}
                          </optgroup>

                          <optgroup
                            label={t(
                              "home.quickBooking.destinations"
                            )}
                          >
                            {destinationOptions
                              .filter(
                                (option) =>
                                  option.type === "destination"
                              )
                              .map((option) => (
                                <option
                                  key={`to-destination-${option.value}`}
                                  value={option.value}
                                >
                                  {option.label}
                                </option>
                              ))}
                          </optgroup>
                        </select>
                      </div>
                    </label>

                    <label className="quick-field">
                      <span>{t("home.quickBooking.date")}</span>

                      <div className="quick-input-wrap quick-datepicker-wrap">
                        <CalendarDays size={18} />

                        <DatePicker
                          selected={
                            quickForm.tripDate
                              ? new Date(
                                  `${quickForm.tripDate}T12:00:00`
                                )
                              : null
                          }
                          onChange={(date: Date | null) => {
                            setQuickForm((prev) => ({
                              ...prev,
                              tripDate: date
                                ? `${date.getFullYear()}-${String(
                                    date.getMonth() + 1
                                  ).padStart(2, "0")}-${String(
                                    date.getDate()
                                  ).padStart(2, "0")}`
                                : "",
                            }));
                          }}
                          dateFormat="dd/MM/yyyy"
                          minDate={new Date()}
                          placeholderText={t(
                            "home.quickBooking.chooseDate"
                          )}
                          className="quick-datepicker-input"
                          calendarClassName="quick-datepicker-calendar"
                          popperClassName="quick-datepicker-popper"
                          popperPlacement="top-start"
                        />
                      </div>
                    </label>

                    <label className="quick-field">
                      <span>
                        {t("home.quickBooking.passengers")}
                      </span>

                      <div className="quick-input-wrap">
                        <Users size={18} />

                        <select
                          name="passengers"
                          value={quickForm.passengers}
                          onChange={handleQuickChange}
                        >
                          {Array.from(
                            { length: 8 },
                            (_, index) => index + 1
                          ).map((passengerCount) => (
                            <option
                              key={passengerCount}
                              value={passengerCount}
                            >
                              {t(
                                "home.quickBooking.passenger",
                                {
                                  count: passengerCount,
                                }
                              )}
                            </option>
                          ))}
                        </select>
                      </div>
                    </label>

                    <button
                      className="quick-booking-button"
                      type="submit"
                    >
                      {t("home.quickBooking.book")}

                      <ArrowRight size={18} />
                    </button>
                  </div>
                </form>
              </SlideUp>
            </div>
          </SlideInLeft>
        </div>
      </div>

      <section className="popular-routes-section">
        <div className="popular-routes-container">
          <FadeIn>
            <div className="popular-routes-header">
              <span className="popular-routes-eyebrow">
                Faro Airport Transfers
              </span>

              <h2 className="popular-routes-title">
                Popular Faro Airport Transfers
              </h2>

              <p className="popular-routes-subtitle">
                Explore some of our most booked private transfer routes from Faro
                Airport, with fixed prices and direct door-to-door service.
              </p>
            </div>
          </FadeIn>

          <StaggerContainer
            delay={0.15}
            staggerDelay={0.08}
            className="popular-routes-grid"
          >
            {popularRoutes.map((route) => (
              <Link
                key={route.slug}
                to={`/routes/${route.slug}`}
                className="popular-route-card"
              >
                <img
      src={route.image}
      alt={`${route.destination} private transfer from Faro Airport`}
      className="popular-route-image"
      loading="lazy"
    />
                <div className="popular-route-card-top">
                  <div className="popular-route-icon">
                    <Plane size={22} />
                  </div>

                  <span className="popular-route-from">
                    Faro Airport
                  </span>
                </div>

                <div className="popular-route-destination">
                  <MapPin size={20} />
                  <h3>{route.destination}</h3>
                </div>

                <div className="popular-route-meta">
                  <span>
                    <BadgeDollarSign size={17} />
                    From {route.price}
                  </span>

                  <span>
                    <Clock3 size={17} />
                    {route.duration}
                  </span>
                </div>

                <div className="popular-route-link-label">
                  View transfer
                  <ArrowRight size={18} />
                </div>
              </Link>
            ))}
          </StaggerContainer>

          <div className="popular-routes-all-link-wrap">
            <Link to="/prices" className="popular-routes-all-link">
              View all transfer prices
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>


      <section
        ref={featuresRef}
        className={`hero-features-section ${
          featuresVisible ? "shine-active" : ""
        }`}
      >
        <div className="hero-features-container">
          <FadeIn>
            <div className="hero-features-header">
              <h2 className="hero-features-title">
                {t("home.featuresSection.title")}
              </h2>

              <p className="hero-features-subtitle">
                {t("home.featuresSection.subtitle")}
              </p>
            </div>
          </FadeIn>

          <StaggerContainer
            delay={0.2}
            staggerDelay={0.15}
            className="hero-features"
          >
            {features.map((feature, index) => (
              <div
                key={index}
                className="hero-feature-item"
              >
                <ScaleIn delay={index * 0.1}>
                  <div className="hero-feature-icon">
                    {feature.icon}
                  </div>
                </ScaleIn>

                <h3 className="hero-feature-title">
                  {feature.title}
                </h3>

                <p className="hero-feature-description">
                  {feature.description}
                </p>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section
        className="hero-reviews-section"
        style={{
          backgroundImage: `
            linear-gradient(
              135deg,
              rgba(6,18,24,0.84),
              rgba(8,35,39,0.76),
              rgba(6,18,24,0.88)
            ),
            radial-gradient(
              circle at 18% 18%,
              rgba(20,184,166,0.28),
              transparent 32%
            ),
            url("/reviews-bg.jpg")
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="hero-reviews-container">
          <FadeIn>
            <div className="hero-reviews-header">
              <span className="hero-reviews-eyebrow">
                {t("home.reviews.eyebrow")}
              </span>

              <h2 className="hero-reviews-title">
                {t("home.reviews.title")}
              </h2>

              <p className="hero-reviews-subtitle">
                {t("home.reviews.subtitle")}
              </p>
            </div>
          </FadeIn>

          <StaggerContainer
            delay={0.2}
            staggerDelay={0.15}
            className="hero-reviews-grid"
          >
            {reviews.map((review, index) => (
              <div
                key={index}
                className={`hero-review-card ${
                  index === 1 ? "featured" : ""
                }`}
              >
                <div className="hero-review-top">
                  <div className="hero-review-avatar-icon">
                    <User size={24} />
                  </div>

                  <div>
                    <h3 className="hero-review-name">
                      {review.name}
                    </h3>

                    <p className="hero-review-location">
                      {review.location}
                    </p>
                  </div>
                </div>

                <div className="hero-review-rating">
                  {Array.from({
                    length: review.rating,
                  }).map((_, starIndex) => (
                    <Star
                      key={starIndex}
                      size={18}
                      className="hero-review-star"
                    />
                  ))}
                </div>

                <p className="hero-review-text">
                  “{review.text}”
                </p>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </>
  );
}